import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingSeaPointPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Sea Point Professional Painters - Apartment & Residential Painting | Commercial Specialists',
    description: 'Expert painting services in Sea Point, Green Point, and Bantry Bay. Specialized in apartment, residential, and commercial painting. Over 17 years serving the Atlantic Seaboard with quality finishes.',
    keywords: 'painters Sea Point, apartment painting Sea Point, house painters Sea Point, residential painting, commercial painting, Atlantic Seaboard painters',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Sea Point',
          description: 'Professional apartment and residential painting',
          address: 'Sea Point, Cape Town',
          priceRange: '$$',
          serviceArea: ['Sea Point', 'Green Point', 'Bantry Bay', 'Fresnaye', 'Three Anchor Bay', 'Mouille Point']
        }),
        generateServiceSchema({
          name: 'Apartment & Residential Painting',
          description: 'Complete painting solutions for apartments and homes',
          provider: 'Painters Sea Point',
          areaServed: ['Sea Point', 'Green Point', 'Bantry Bay'],
          serviceType: ['Apartment Painting', 'Residential Painting', 'Commercial Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters Sea Point",
    location: "Sea Point, Cape Town",
    website: "https://paintersseapoint.co.za",
    heroImage: "https://public.readdy.ai/ai/img_res/5b9bb62bedb9f3c1c0d4e32052917d15.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=apartment%20block%20exterior%20painting%20scaffolding%20professional%20painters%20working%20on%20coastal%20building%20white%20walls%20ocean%20view%20Sea%20Point%20promenade&width=800&height=600&seq=seapoint-gallery1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=commercial%20shopfront%20painting%20work%20professional%20team%20applying%20fresh%20paint%20to%20retail%20store%20facade%20modern%20design%20Sea%20Point%20main%20road&width=800&height=600&seq=seapoint-gallery2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=apartment%20interior%20freshly%20painted%20living%20room%20ocean%20views%20modern%20furniture%20clean%20white%20walls%20professional%20finish%20coastal%20property&width=800&height=600&seq=seapoint-gallery3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=building%20facade%20coastal%20weather%20protection%20coating%20application%20professional%20painters%20working%20on%20exterior%20walls%20salt%20damage%20prevention%20ocean%20backdrop&width=800&height=600&seq=seapoint-gallery4&orientation=landscape"
    ],
    services: [
      {
        title: "Apartment Block Painting",
        description: "Comprehensive painting services for apartment buildings and residential complexes. We specialize in large-scale projects with minimal disruption to residents, working efficiently while maintaining the highest quality standards.",
        features: ["Exterior facade painting", "Common area painting", "Individual unit painting", "Body corporate coordination"]
      },
      {
        title: "Coastal Weather Protection",
        description: "Specialized protective coatings and treatments designed for coastal properties. Our solutions protect against salt damage, wind erosion, and moisture penetration, extending the life of your paintwork.",
        features: ["Marine-grade coatings", "Salt damage prevention", "Waterproofing treatments", "UV protection systems"]
      },
      {
        title: "Commercial Shopfronts",
        description: "Professional painting services for retail stores, restaurants, and commercial properties. We understand the importance of maintaining your business image and work around your operating hours.",
        features: ["Shopfront painting", "Signage preparation", "After-hours service", "Quick turnaround times"]
      }
    ],
    serviceAreas: ["Sea Point", "Bantry Bay", "Green Point", "Fresnaye", "Three Anchor Bay", "Mouille Point"],
    about: "Painters Sea Point is a leading painting contractor specializing in apartment blocks, commercial properties, and coastal weather protection in Cape Town's vibrant Sea Point area. With over 15 years of experience in coastal painting, we have established ourselves as the trusted choice for body corporates, property managers, and business owners along the Atlantic Seaboard.",
    aboutExtended: [
      "Our expertise extends across all aspects of coastal property painting, from large apartment complexes to individual shopfronts. We understand the unique challenges of working in Sea Point's coastal environment, where salt air, strong winds, and ocean moisture require specialized painting techniques and premium protective coatings.",
      "What sets us apart is our comprehensive approach to coastal painting. We don't just apply paint – we provide complete protection systems that safeguard your property against the harsh Atlantic coastal conditions. Our team uses only marine-grade paints and coatings specifically formulated to withstand salt damage, UV exposure, and moisture penetration.",
      "We work extensively with body corporates and property management companies, coordinating large-scale projects with minimal disruption to residents and businesses. Our project managers ensure clear communication, efficient scheduling, and quality control throughout every phase of the work. Whether it's a complete building exterior repaint or individual unit renovations, we deliver exceptional results on time and within budget."
    ],
    ourApproach: [
      "Comprehensive property assessment and planning",
      "Thorough surface preparation and repairs",
      "Marine-grade coastal protection coatings",
      "Professional application with minimal disruption",
      "Body corporate and tenant coordination",
      "Complete cleanup and final inspection"
    ],
    whyChoose: [
      "15+ years coastal painting experience",
      "Apartment block specialists",
      "Marine-grade materials",
      "Body corporate approved",
      "Commercial property experts",
      "Satisfaction guaranteed"
    ],
    testimonials: [
      {
        name: "Jennifer Abrahams",
        rating: 5,
        text: "Excellent service for our apartment block. The team was professional, worked efficiently, and the building looks fantastic. They coordinated perfectly with our body corporate."
      },
      {
        name: "Robert Levy",
        rating: 5,
        text: "As a restaurant owner, I needed my shopfront painted quickly without disrupting business. They worked after hours and delivered perfect results. Highly recommended!"
      },
      {
        name: "Susan Daniels",
        rating: 5,
        text: "The coastal protection coating they applied has made a huge difference. No more salt damage and the paint still looks fresh after two years. Great value for money."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Sea Point</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Sea Point</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following areas in Cape Town's Atlantic Seaboard:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer comprehensive painting services tailored for coastal properties in Sea Point.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Sea Point, Bantry Bay, Green Point</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 7:30AM-5:30PM</p>
                    <p className="text-gray-600">Sat: 8AM-1PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Sea Point</li>
                <li>Green Point</li>
                <li>Bantry Bay</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
